import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { CreditCard, Globe2, Instagram, ShieldCheck, AlertTriangle, Mail, Send, Webhook, Copy, RefreshCw, QrCode } from "lucide-react";
import SaveIndicator from "@/components/SaveIndicator";
import { useAutoSave } from "@/lib/useAutoSave";
import { QRCodeSVG } from "qrcode.react";

// Public origin, needed for building QR URLs and the webhook endpoint hint. Falls
// back to window.location.origin when REACT_APP_BACKEND_URL isn't set (dev mode).
const PUBLIC_HOST =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

// -------------------- Webhooks + QR tab --------------------
function IntegrationsTab({ publicBase }) {
  const [creds, setCreds] = useState(null);         // {webhook_url_path, secret}
  const [rotating, setRotating] = useState(false);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    api.get("/integrations/webhook-secret").then((r) => setCreds(r.data)).catch(() => {});
  }, []);

  const rotate = async () => {
    if (!window.confirm("Rotate the webhook secret? The old value will stop working immediately — you'll need to update Nifty (or any external tool) with the new one.")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/integrations/webhook-secret/rotate", {});
      setCreds(data);
      toast.success("secret rotated. update the external tool now.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "rotation failed.");
    } finally {
      setRotating(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`${label} copied.`),
      () => toast.error("could not copy — please select manually."),
    );
  };

  const webhookUrl = creds ? `${publicBase}${creds.webhook_url_path}` : "";
  const captureUrl = `${publicBase}/mobile/capture`;

  return (
    <div className="space-y-8" data-testid="biz-integrations-section">
      {/* Webhook */}
      <section className="bg-linen border border-taupe/50 p-8">
        <div className="flex items-start gap-4 mb-6">
          <Webhook className="w-6 h-6 text-brass mt-1" />
          <div>
            <h2 className="font-serif text-2xl lowercase text-charcoal">mark-sold webhook</h2>
            <p className="text-xs text-espresso mt-1">give nifty (or zapier / n8n) this URL + secret so the moment a piece sells externally, maison pilet auto-marks it sold and moves it to <em>sale completion</em>.</p>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <p className="editorial-label mb-2">POST url</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-ivory border border-taupe/40 px-3 py-2 text-sm text-charcoal font-mono break-all" data-testid="webhook-url">{webhookUrl || "loading…"}</code>
              <button onClick={() => copy(webhookUrl, "URL")} disabled={!creds} className="border border-taupe hover:border-brass hover:text-brass p-2 disabled:opacity-40" data-testid="copy-webhook-url" title="copy url">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <p className="editorial-label mb-2">header · X-Webhook-Secret</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-ivory border border-taupe/40 px-3 py-2 text-sm text-charcoal font-mono break-all" data-testid="webhook-secret">
                {!creds ? "loading…" : reveal ? creds.secret : creds.secret.slice(0, 8) + "…" + creds.secret.slice(-4)}
              </code>
              <button onClick={() => setReveal((v) => !v)} className="border border-taupe hover:border-brass hover:text-brass px-3 py-2 text-[0.65rem] tracking-widest uppercase" data-testid="reveal-secret">
                {reveal ? "hide" : "reveal"}
              </button>
              <button onClick={() => copy(creds?.secret || "", "secret")} disabled={!creds} className="border border-taupe hover:border-brass hover:text-brass p-2 disabled:opacity-40" data-testid="copy-webhook-secret" title="copy secret">
                <Copy className="w-4 h-4" />
              </button>
              <button onClick={rotate} disabled={rotating || !creds} className="border border-brass text-brass hover:bg-brass hover:text-ivory px-3 py-2 text-[0.65rem] tracking-widest uppercase disabled:opacity-40 flex items-center gap-1" data-testid="rotate-secret">
                <RefreshCw className={`w-3 h-3 ${rotating ? "animate-spin" : ""}`} /> rotate
              </button>
            </div>
          </div>

          <div className="bg-ivory border border-taupe/40 p-4">
            <p className="editorial-label mb-2">expected JSON body</p>
            <pre className="text-xs text-charcoal font-mono whitespace-pre-wrap">{`{
  "sku": "0042",
  "sold_price": 148.00,
  "channel": "nifty",
  "buyer_name": "optional",
  "external_order_id": "optional",
  "sold_at": "optional ISO-8601"
}`}</pre>
            <p className="text-[0.65rem] uppercase tracking-widest text-taupe mt-3">only <code>sku</code> is required. repeat calls on the same sku are safe (idempotent).</p>
          </div>
        </div>
      </section>

      {/* QR code */}
      <section className="bg-linen border border-taupe/50 p-8">
        <div className="flex items-start gap-4 mb-6">
          <QrCode className="w-6 h-6 text-brass mt-1" />
          <div>
            <h2 className="font-serif text-2xl lowercase text-charcoal">sourcing kit qr code</h2>
            <p className="text-xs text-espresso mt-1">print + stick this on your sourcing tote. one scan on any iphone opens the rapid capture page.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-8">
          <div id="mp-qr-card" className="bg-ivory border border-taupe/40 p-6 flex flex-col items-center gap-3" data-testid="capture-qr">
            <QRCodeSVG value={captureUrl} size={200} bgColor="#F6F2EB" fgColor="#2E2C2A" level="H" includeMargin={false} />
            <p className="editorial-label text-center">scan · rapid capture</p>
            <p className="text-[0.6rem] uppercase tracking-widest text-taupe text-center">maison pilet — one thumb sourcing</p>
          </div>
          <div className="flex-1 min-w-[220px] space-y-3">
            <div>
              <p className="editorial-label mb-1">encoded url</p>
              <code className="block bg-ivory border border-taupe/40 px-3 py-2 text-sm text-charcoal font-mono break-all">{captureUrl}</code>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => window.print()}
                className="border border-brass text-brass hover:bg-brass hover:text-ivory px-4 py-2 text-[0.7rem] tracking-widest uppercase flex items-center gap-2"
                data-testid="print-qr"
              >
                <QrCode className="w-3 h-3" /> print
              </button>
              <button
                onClick={() => copy(captureUrl, "URL")}
                className="border border-taupe hover:border-brass hover:text-brass px-4 py-2 text-[0.7rem] tracking-widest uppercase flex items-center gap-2"
                data-testid="copy-capture-url"
              >
                <Copy className="w-3 h-3" /> copy url
              </button>
            </div>
            <p className="text-xs text-espresso">unauthenticated visitors will be prompted to log in first — after that they land straight on the capture page. paired well with an installed pwa icon on the home screen.</p>
          </div>
        </div>
      </section>

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #mp-qr-card, #mp-qr-card * { visibility: visible !important; }
          #mp-qr-card { position: absolute; left: 50%; top: 40%; transform: translate(-50%, -50%); border: none !important; }
        }
      `}</style>
    </div>
  );
}

export default function AdminBusiness() {
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("profile");
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  const buildPayload = (snap) => {
    const payload = { ...snap };
    delete payload.id;
    delete payload.updated_at;
    return payload;
  };

  const autoSave = useAutoSave({
    value: s,
    save: async (snap) => {
      if (!snap) return snap;
      await api.put("/settings", buildPayload(snap));
      await refresh();
      return snap;
    },
    debounceMs: 1500,
  });

  if (!s) return <AdminLayout><p className="editorial-label">loading…</p></AdminLayout>;

  const upd = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post("/email/test", {});
      if (data.mock) toast.success(`test logged (mock) → ${data.recipient}`);
      else toast.success(`test sent → ${data.recipient}`);
    } catch (e) {
      toast.error("test failed: " + (e.response?.data?.detail || "check logs"));
    } finally {
      setSendingTest(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", buildPayload(s));
      await refresh();
      autoSave.markBaseline(s);
      toast.success("business profile saved.");
    } catch {
      toast.error("save failed.");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (k, label, type = "text", placeholder = "") => (
    <div>
      <label className="editorial-label">{label}</label>
      <div className="flex items-center gap-2 border-b border-taupe mt-2">
        <input data-testid={`biz-${k}`} type={type} placeholder={placeholder} value={s[k] ?? ""} onChange={(e) => upd(k, e.target.value)} className="w-full bg-transparent py-2 focus:outline-none text-charcoal" />
      </div>
    </div>
  );

  const tabs = [
    { id: "profile", label: "company profile" },
    { id: "stripe", label: "stripe (mock)" },
    { id: "ebay", label: "ebay" },
    { id: "instagram", label: "instagram" },
    { id: "email", label: "email · resend" },
    { id: "integrations", label: "webhooks & qr" },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-business" className="max-w-5xl">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="editorial-label">business · profile & integrations</span>
            <h1 className="font-serif text-5xl lowercase text-charcoal mt-3">the house, in the world.</h1>
            <div className="mt-3">
              <SaveIndicator status={autoSave.status} lastSavedAt={autoSave.lastSavedAt} lastError={autoSave.lastError} flush={autoSave.flush} testId="biz-save-indicator" />
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="business-save" className="bg-brass hover:bg-espresso text-ivory px-8 py-3 text-xs tracking-[0.25em] uppercase">
            {saving ? "saving…" : "save"}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-8 border-b border-taupe/50 mb-10">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`pb-4 text-xs tracking-[0.25em] uppercase border-b-2 transition-colors ${
                tab === t.id ? "border-brass text-brass" : "border-transparent text-charcoal hover:text-brass"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "profile" && (
          <section className="bg-linen border border-taupe/50 p-8 space-y-8" data-testid="biz-profile-section">
            <div>
              <h2 className="font-serif text-2xl lowercase text-charcoal mb-1">identity</h2>
              <p className="text-xs text-espresso mb-6">how the house presents itself across every surface — navbar, footer, receipts, listings.</p>
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                {renderField("legal_business_name", "legal business name")}
                {renderField("tagline", "public tagline")}
                {renderField("support_email", "support email", "email")}
                {renderField("contact_phone", "contact phone")}
                <div className="md:col-span-2">{renderField("physical_address", "physical address")}</div>
              </div>
            </div>

            <div className="pt-6 border-t border-taupe/40">
              <h2 className="font-serif text-2xl lowercase text-charcoal mb-1">social handles</h2>
              <p className="text-xs text-espresso mb-6">shown publicly in the storefront footer.</p>
              <div className="grid md:grid-cols-3 gap-6">
                {renderField("instagram_handle", "instagram", "text", "@maisonpilet")}
                {renderField("facebook_handle", "facebook")}
                {renderField("pinterest_handle", "pinterest")}
              </div>
            </div>

            <p className="text-xs text-espresso pt-4 border-t border-taupe/40">
              tip · upload your logo, favicon and placeholder under <span className="text-brass">institutional settings</span> — they&apos;re rendered live everywhere.
            </p>

            {/* Workflow prefs — added after the Nifty pivot to give her a
                one-toggle control over whether the in-app AI listing shortcuts
                still show. Default OFF (hidden) because Nifty handles it. */}
            <div className="pt-6 border-t border-taupe/40">
              <h2 className="font-serif text-2xl lowercase text-charcoal mb-1">workflow</h2>
              <p className="text-xs text-espresso mb-6">tune which listing tools appear on the inventory edit page.</p>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="hide-ai-tools-toggle">
                <input
                  type="checkbox"
                  checked={!!s?.hide_ai_listing_tools}
                  onChange={(e) => setS((prev) => ({ ...prev, hide_ai_listing_tools: e.target.checked }))}
                  className="mt-1 accent-brass"
                  data-testid="hide-ai-tools-checkbox"
                />
                <div>
                  <p className="text-sm text-charcoal">hide in-app AI listing generators (instagram, poshmark, description writer)</p>
                  <p className="text-xs text-espresso mt-1">
                    recommended when nifty is your primary lister — nifty already generates titles, descriptions, measurements and per-platform copy. turning this on keeps the inventory edit page focused on the fields nifty can't fill (sourcing cost, direct storefront photos, etc.).
                  </p>
                </div>
              </label>
            </div>
          </section>
        )}

        {tab === "stripe" && (
          <section className="bg-linen border border-taupe/50 p-8" data-testid="biz-stripe-section">
            <div className="flex items-start gap-4 mb-8">
              <CreditCard className="w-6 h-6 text-brass mt-1" />
              <div>
                <h2 className="font-serif text-2xl lowercase text-charcoal">stripe</h2>
                <p className="text-xs text-espresso">accept payments via stripe checkout. currently in MOCK mode — no real charges are made.</p>
              </div>
            </div>
            <div className="bg-ivory border border-brass/60 p-4 mb-6 flex items-start gap-3" data-testid="stripe-mock-banner">
              <AlertTriangle className="w-4 h-4 text-brass mt-0.5" />
              <p className="text-xs text-charcoal leading-relaxed">
                <strong className="text-brass">mock mode active.</strong> all checkouts redirect to an internal mock success page.
                paste real keys and switch mode to “live” to activate stripe.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              {renderField("stripe_publishable_key", "publishable key", "text", "pk_test_… (mock)")}
              {renderField("stripe_secret_key", "secret key", "password", "sk_test_… (mock)")}
              <div>
                <label className="editorial-label">mode</label>
                <select data-testid="biz-stripe_mode" value={s.stripe_mode} onChange={(e) => upd("stripe_mode", e.target.value)} className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal">
                  <option value="test">test (mock)</option>
                  <option value="live">live</option>
                </select>
              </div>
            </div>
          </section>
        )}

        {tab === "ebay" && (
          <section className="bg-linen border border-taupe/50 p-8" data-testid="biz-ebay-section">
            <div className="flex items-start gap-4 mb-8">
              <Globe2 className="w-6 h-6 text-brass mt-1" />
              <div>
                <h2 className="font-serif text-2xl lowercase text-charcoal">ebay</h2>
                <p className="text-xs text-espresso">cross-post listings to ebay via the production inventory api. connect and manage the seller account in <a href="/admin/settings" className="text-brass underline">studio settings → ebay</a>.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              {renderField("ebay_app_id", "app id")}
              {renderField("ebay_cert_id", "cert id", "password")}
              {renderField("ebay_dev_id", "dev id")}
              {renderField("ebay_oauth_token", "user oauth token", "password")}
              <div>
                <label className="editorial-label">environment</label>
                <select data-testid="biz-ebay_sandbox" value={String(s.ebay_sandbox)} onChange={(e) => upd("ebay_sandbox", e.target.value === "true")} className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal">
                  <option value="true">sandbox (mock)</option>
                  <option value="false">production</option>
                </select>
              </div>
            </div>
            <p className="editorial-label mt-8">these legacy fields are retained for reference — the live oauth flow lives in studio settings → ebay.</p>
          </section>
        )}

        {tab === "instagram" && (
          <section className="bg-linen border border-taupe/50 p-8" data-testid="biz-instagram-section">
            <div className="flex items-start gap-4 mb-8">
              <Instagram className="w-6 h-6 text-brass mt-1" />
              <div>
                <h2 className="font-serif text-2xl lowercase text-charcoal">instagram</h2>
                <p className="text-xs text-espresso">
                  the primary path is the <strong className="text-charcoal">ai copy-paste generator</strong> — open any piece,
                  scroll to <em>omni-channel sync</em>, click <em>instagram</em>. gemini writes an editorial caption + hashtags,
                  the photos are packaged as a zip, and you paste on your phone. no meta app review required.
                </p>
              </div>
            </div>
            <div className="bg-ivory border border-brass/40 p-5 mb-8">
              <p className="editorial-label mb-2 text-brass">how the copy-paste flow works</p>
              <ol className="text-sm text-charcoal leading-relaxed list-decimal list-inside space-y-1">
                <li>open any inventory item</li>
                <li>scroll to <em>omni-channel sync</em> and click the <em>instagram</em> tile</li>
                <li>choose a tone (boutique · luxury · casual · sustainable · promotional) and click <em>generate caption</em></li>
                <li>copy the paste-ready block, download the photos zip, airdrop to your phone</li>
                <li>paste in the instagram app · post · come back and click <em>mark as posted</em> — the piece drops out of the queue</li>
              </ol>
            </div>
            <p className="editorial-label mb-4">graph api (optional · pending meta app review)</p>
            <p className="text-xs text-espresso mb-6">these fields are for the eventual direct-post path. leave blank while using the copy-paste flow.</p>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              {renderField("instagram_business_id", "business account id")}
              {renderField("instagram_access_token", "long-lived access token", "password")}
            </div>
            <p className="editorial-label mt-8 flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> stored encrypted at rest · used only for cross-posting</p>
          </section>
        )}

        {tab === "email" && (
          <section className="bg-linen border border-taupe/50 p-8" data-testid="biz-email-section">
            <div className="flex items-start gap-4 mb-8">
              <Mail className="w-6 h-6 text-brass mt-1" />
              <div>
                <h2 className="font-serif text-2xl lowercase text-charcoal">email notifications</h2>
                <p className="text-xs text-espresso">when a customer submits an enquiry, we&apos;ll email it to your <span className="text-brass">support email</span> automatically.</p>
              </div>
            </div>

            <div className="bg-ivory border border-brass/60 p-4 mb-6 flex items-start gap-3" data-testid="email-mode-banner">
              <AlertTriangle className="w-4 h-4 text-brass mt-0.5" />
              <p className="text-xs text-charcoal leading-relaxed">
                {s.resend_api_key
                  ? <><strong className="text-brass">live mode.</strong> emails will be delivered via resend.</>
                  : <><strong className="text-brass">mock mode.</strong> no api key set — every notification is logged to the backend instead of being sent. paste a resend key to go live.</>}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              {renderField("resend_api_key", "resend api key", "password", "re_… (leave blank for mock)")}
              {renderField("email_from", "from address", "email", "onboarding@resend.dev")}
              <div className="md:col-span-2 flex items-center gap-4">
                <label className="editorial-label">notifications</label>
                <button
                  type="button"
                  onClick={() => upd("email_notifications_enabled", !s.email_notifications_enabled)}
                  data-testid="biz-email_notifications_enabled"
                  className={`relative inline-flex h-6 w-12 items-center transition-colors ${s.email_notifications_enabled ? "bg-brass" : "bg-taupe"}`}
                >
                  <span className={`inline-block h-4 w-4 bg-ivory transition-transform ${s.email_notifications_enabled ? "translate-x-7" : "translate-x-1"}`} />
                </button>
                <span className="text-xs text-charcoal">{s.email_notifications_enabled ? "enabled" : "paused"}</span>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-taupe/40 flex items-center justify-between">
              <div>
                <p className="editorial-label">test the wiring</p>
                <p className="text-xs text-espresso mt-2">sends a test message to your support email ({s.support_email || "set support_email first"}).</p>
              </div>
              <button onClick={sendTestEmail} disabled={sendingTest || !s.support_email} data-testid="email-test-btn" className="border border-brass text-brass hover:bg-brass hover:text-ivory px-6 py-3 text-xs tracking-[0.25em] uppercase transition-colors disabled:opacity-50 flex items-center gap-2">
                <Send className="w-3 h-3" /> {sendingTest ? "sending…" : "send test"}
              </button>
            </div>

            <p className="editorial-label mt-8 flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> get your api key at resend.com → api keys</p>
          </section>
        )}
        {tab === "integrations" && s && <IntegrationsTab publicBase={PUBLIC_HOST} />}
      </div>
    </AdminLayout>
  );
}
